import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, DollarSign, ClipboardList, Bell, BookOpen, X, CheckCheck, CheckCircle, Clock, AlertCircle, CalendarRange } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import StatCard from '../../components/common/StatCard';
import { formatDate, formatCurrency } from '../../utils/helpers';
import { api } from '../../utils/api';
import { getWeekRange } from '../../utils/homeworkFeedback';

// Subject color palette
const SUBJECT_COLORS = [
  { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', icon: 'text-blue-500' },
  { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-700', dot: 'bg-green-500', icon: 'text-green-500' },
  { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', icon: 'text-blue-500' },
  { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', icon: 'text-orange-500' },
  { bg: 'bg-pink-50', border: 'border-pink-200', badge: 'bg-pink-100 text-pink-700', dot: 'bg-pink-500', icon: 'text-pink-500' },
  { bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-100 text-teal-700', dot: 'bg-teal-500', icon: 'text-teal-500' },
];

const ratingColor = (r) => r === 'good' ? 'text-green-700 bg-green-100 border-green-300'
  : r === 'average' ? 'text-amber-700 bg-amber-100 border-amber-300'
  : r === 'needs_improvement' ? 'text-red-700 bg-red-100 border-red-300' : '';
const ratingLabel = (r) => r === 'good' ? 'Good' : r === 'average' ? 'Average' : r === 'needs_improvement' ? 'Needs Work' : '';

// feedback is the real, teacher-recorded row from homework_submissions
// (status/handwriting/spelling/neatness/remarks) — replaces the old
// "Mark Done Today" self-report, which only ever wrote to browser
// localStorage and never reached the backend at all.
function HomeworkCard({ hw, colorIdx, feedback }) {
  const today = new Date().toISOString().split('T')[0];
  const status = feedback?.status || 'pending';
  const isOverdue = hw.dueDate < today && status !== 'completed';
  const isDueToday = hw.dueDate === today;
  const color = SUBJECT_COLORS[colorIdx % SUBJECT_COLORS.length];
  const hasRatings = feedback && (feedback.handwriting || feedback.spelling || feedback.neatness);

  return (
    <div className={`rounded-xl border ${color.border} ${color.bg} overflow-hidden`}>
      {/* Header row */}
      <div className="flex items-start gap-3 p-4">
        <div className={`w-1 self-stretch rounded-full ${color.dot} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold truncate">{hw.title}</h4>
              <p className="mt-0.5 line-clamp-2">{hw.description}</p>
            </div>
            <span className={`font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
              isOverdue ? 'bg-red-100 text-red-600' :
              isDueToday ? 'bg-amber-100 text-amber-700' :
              'bg-gray-100'
            }`}>
              {isDueToday ? 'Due Today' : isOverdue ? 'Overdue' : `Due ${formatDate(hw.dueDate)}`}
            </span>
          </div>
          <div className="flex items-center justify-between mt-3 gap-2">
            <span className="">Assigned: {formatDate(hw.assignedDate)}</span>
            <span className={`flex items-center gap-1 px-2 py-1 rounded-lg font-medium ${
              status === 'completed' ? 'text-green-700 bg-green-100' : status === 'late' ? 'text-amber-700 bg-amber-100' : 'text-gray-500 bg-gray-100'
            }`}>
              <CheckCircle size={13} /> {status === 'completed' ? 'Completed' : status === 'late' ? 'Late' : 'Pending'}
            </span>
          </div>
        </div>
      </div>

      {/* Teacher's Feedback — real data, only shown once the teacher has recorded something */}
      {(hasRatings || feedback?.remarks) && (
        <div className="px-4 pb-3 pt-0 border-t border-opacity-50" style={{ borderColor: 'inherit' }}>
          <p className="font-medium mb-2 mt-2">Teacher's Feedback</p>
          {hasRatings && (
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {['handwriting','spelling','neatness'].map(k => feedback[k] && (
                <span key={k} className={`text-xs font-medium px-2 py-0.5 rounded-full border ${ratingColor(feedback[k])}`}>
                  {k[0].toUpperCase() + k.slice(1)}: {ratingLabel(feedback[k])}
                </span>
              ))}
            </div>
          )}
          {feedback?.remarks && <p className="italic text-gray-700">{feedback.remarks}</p>}
        </div>
      )}
    </div>
  );
}

function ParentDashboard() {
  const { students, parents, classes, attendance, feePayments, feeStructures, exams, homework,
    subjects, announcements, notifications, setNotifications } = useApp();
  const { currentUser } = useAuth();

  const parent = parents.find(p => p.id === currentUser?.profileId);
  const myChildren = students.filter(s => parent?.studentIds?.includes(s.id));

  const [selectedChildId, setSelectedChildId] = useState(myChildren[0]?.id || null);
  const child = myChildren.find(c => c.id === selectedChildId) || myChildren[0];

  // Real, database-backed teacher feedback for this child — replaces the
  // old hwCompletions self-report, which never reached the backend at all.
  const [myFeedback, setMyFeedback] = useState({}); // homeworkId -> feedback row
  useEffect(() => {
    if (!child?.id) { setMyFeedback({}); return; }
    let cancelled = false;
    api.get(`/students/${child.id}/homework-submissions`).then(rows => {
      if (cancelled) return;
      const map = {};
      (Array.isArray(rows) ? rows : []).forEach(r => { if (r.homeworkId) map[r.homeworkId] = r; });
      setMyFeedback(map);
    }).catch(() => { if (!cancelled) setMyFeedback({}); });
    return () => { cancelled = true; };
  }, [child?.id]);
  const childClass = classes.find(c => c.id === child?.classId);
  const childAttendance = attendance.filter(a => a.entityType === 'student' && a.entityId === child?.id);
  const present = childAttendance.filter(a => a.status === 'present').length;
  const attPct = childAttendance.length > 0 ? Math.round(present / childAttendance.length * 100) : 0;
  const myFeeStructure = feeStructures.find(f => f.classId === childClass?.name);
  // myPaid (cash + discount) feeds the Balance math below; myCashPaid (cash
  // only) is what's shown in the "Paid" label — a discount isn't money that
  // was actually handed over, so it must not inflate the displayed figure.
  const myCashPaid = feePayments.filter(p => p.studentId === child?.id).reduce((s, p) => s + Number(p.amount || 0), 0);
  const myPaid = myCashPaid + feePayments.filter(p => p.studentId === child?.id).reduce((s, p) => s + Number(p.discount || 0), 0);
  const totalDue = myFeeStructure ? myFeeStructure.totalAmount + (child?.previousYearBalance || 0) : 0;
  const feePending = myFeeStructure ? Math.max(0, totalDue - myPaid) : 0;
  const upcomingExams = exams.filter(e => e.status === 'scheduled' || e.status === 'ongoing').slice(0, 3);
  const myAnnouncements = announcements.filter(a => a.isActive && (a.targetRoles.includes('parent') || a.targetRoles.includes('all')));

  // Active homework for child's class grouped by subject
  const today = new Date().toISOString().split('T')[0];
  const classHomework = homework.filter(h => h.classId === child?.classId && h.status === 'active');

  // Group by subject
  const bySubject = classHomework.reduce((acc, hw) => {
    const subId = hw.subjectId;
    if (!acc[subId]) acc[subId] = [];
    acc[subId].push(hw);
    return acc;
  }, {});
  const subjectIds = Object.keys(bySubject);

  // Homework the teacher hasn't yet marked completed for this child
  const todayPending = classHomework.filter(hw => myFeedback[hw.id]?.status !== 'completed').length;

  // This week's homework, for the dashboard's weekly-report summary tile
  const week = getWeekRange(0);
  const weeklyHw = homework.filter(h => h.classId === child?.classId && h.dueDate >= week.start && h.dueDate <= week.end);
  const weeklyDone = weeklyHw.filter(hw => myFeedback[hw.id]?.status === 'completed').length;
  const weeklyPct = weeklyHw.length > 0 ? Math.round((weeklyDone / weeklyHw.length) * 100) : 0;

  // Unread homework notifications for selected child's class
  const childClassIds = myChildren.map(c => c.classId);
  const hwAlerts = (notifications || []).filter(n =>
    n.type === 'homework' && n.targetRoles.includes('parent') && n.classId === child?.classId
  );
  const unreadAlerts = hwAlerts.filter(n => !n.readBy.includes(currentUser?.id));

  const markRead = (notifId) => {
    setNotifications(prev => prev.map(n =>
      n.id === notifId ? { ...n, readBy: [...n.readBy, currentUser?.id] } : n
    ));
  };
  const markAllRead = () => {
    setNotifications(prev => prev.map(n =>
      (n.type === 'homework' && childClassIds.includes(n.classId) && !n.readBy.includes(currentUser?.id))
        ? { ...n, readBy: [...n.readBy, currentUser?.id] } : n
    ));
  };
  const dismiss = (notifId) => {
    setNotifications(prev => prev.filter(n => n.id !== notifId));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Parent Dashboard</h1>
          {child && <p className="text-gray-500 text-sm mt-0.5">Monitoring: {child.firstName} {child.lastName} | {childClass?.name} - Sec {child.sectionId}</p>}
        </div>
        {myChildren.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {myChildren.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedChildId(c.id)}
                className={`px-4 py-1 rounded-lg text-sm font-medium transition-colors border ${
                  c.id === child?.id
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white border-gray-300 hover:bg-gray-50'
                }`}
              >
                {c.firstName} {c.lastName}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Child Profile */}
      {child && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Child Profile</h3>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xl font-bold">{child.firstName?.[0]}{child.lastName?.[0]}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
              {[['Name', child.firstName + ' ' + child.lastName], ['Class', (childClass?.name || '') + '-' + child.sectionId], ['Roll No', child.rollNumber], ['Admission No', child.admissionNumber]].map(([k, v]) => (
                <div key={k}><p className="">{k}</p><p className="text-sm font-semibold">{v}</p></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* New Homework Alerts Banner */}
      {unreadAlerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-amber-400 rounded-lg flex items-center justify-center">
                <Bell size={16} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900 text-sm">{unreadAlerts.length} New Homework Alert{unreadAlerts.length > 1 ? 's' : ''}</h3>
                <p className="text-amber-600">New homework has been assigned to your child</p>
              </div>
            </div>
            <button onClick={markAllRead} className="flex items-center gap-1 text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg font-medium">
              <CheckCheck size={13} /> Mark all read
            </button>
          </div>
          <div className="space-y-2">
            {unreadAlerts.map(alert => (
              <div key={alert.id} className="flex items-start justify-between bg-white border border-amber-100 rounded-lg p-3 gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <BookOpen size={15} className="text-orange-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{alert.title}</p>
                    <p className="mt-0.5">{alert.body}</p>
                    <p className="mt-0.5">{(() => { const d = new Date(alert.createdAt); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => markRead(alert.id)} className="text-indigo-600 hover:bg-indigo-50 px-2 py-1 rounded font-medium">Read</button>
                  <button onClick={() => dismiss(alert.id)} className="p-1 hover:bg-red-50 rounded hover:text-red-400"><X size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarCheck} label="Attendance" value={attPct + '%'} subtext={present + '/' + childAttendance.length + ' days'} colorClass={attPct >= 75 ? 'bg-green-500' : 'bg-red-500'} />
        <StatCard icon={DollarSign} label="Fee Pending" value={feePending > 0 ? formatCurrency(feePending) : 'Cleared'} subtext="Academic year" colorClass={feePending > 0 ? 'bg-red-500' : 'bg-green-500'} />
        <StatCard icon={ClipboardList} label="Homework" value={classHomework.length} subtext={`${todayPending} pending today`} colorClass="bg-orange-500" />
        <StatCard icon={Bell} label="Announcements" value={myAnnouncements.length} subtext="For parents" colorClass="bg-blue-500" />
      </div>

      {/* Subject-wise Homework with Daily Confirmation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <BookOpen size={18} className="text-indigo-500" />
            Subject-wise Homework
            {classHomework.length > 0 && (
              <span className="bg-indigo-100 text-indigo-700 font-bold px-2 py-0.5 rounded-full">{classHomework.length}</span>
            )}
          </h3>
          {todayPending > 0 && (
            <span className="flex items-center gap-1 font-medium text-amber-700 bg-amber-100 px-3 py-1.5 rounded-lg">
              <AlertCircle size={13} /> {todayPending} not yet marked complete
            </span>
          )}
          {todayPending === 0 && classHomework.length > 0 && (
            <span className="flex items-center gap-1 font-medium text-green-700 bg-green-100 px-3 py-1.5 rounded-lg">
              <CheckCheck size={13} /> All marked complete
            </span>
          )}
        </div>

        {subjectIds.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-6">No active homework assigned</p>
        ) : (
          <div className="space-y-5">
            {subjectIds.map((subId, idx) => {
              const sub = subjects.find(s => s.id === subId);
              const subHws = bySubject[subId];
              const color = SUBJECT_COLORS[idx % SUBJECT_COLORS.length];
              const completedCount = subHws.filter(hw => myFeedback[hw.id]?.status === 'completed').length;

              return (
                <div key={subId}>
                  {/* Subject Header */}
                  <div className={`flex items-center justify-between px-3 py-1 rounded-lg mb-2 ${color.bg} border ${color.border}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${color.dot}`} />
                      <span className="text-sm font-semibold">{sub?.name || 'Unknown Subject'}</span>
                      <span className={`px-2 py-0.5 rounded-full font-medium ${color.badge}`}>{subHws.length} task{subHws.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={12} />
                      <span>{completedCount}/{subHws.length} marked complete</span>
                    </div>
                  </div>

                  {/* Homework cards for this subject */}
                  <div className="space-y-2 pl-2">
                    {subHws.map(hw => (
                      <HomeworkCard
                        key={hw.id}
                        hw={hw}
                        colorIdx={idx}
                        feedback={myFeedback[hw.id]}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* This Week's Homework Report — links to the full per-subject matrix */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <CalendarRange size={18} className="text-indigo-500" />
            This Week's Homework Report
          </h3>
          <Link to="/homework/weekly-report" className="text-sm font-medium text-indigo-600 hover:text-indigo-800">
            View Full Report →
          </Link>
        </div>
        {weeklyHw.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">No homework assigned this week</p>
        ) : (
          <div className="flex items-center gap-4">
            <div className="flex gap-2">
              <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-sm font-medium">{weeklyDone} Done</span>
              <span className="bg-red-100 text-red-600 px-2.5 py-1 rounded-full text-sm font-medium">{weeklyHw.length - weeklyDone} Pending</span>
            </div>
            <div className="flex items-center gap-2 flex-1">
              <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                <div className="h-2 rounded-full bg-green-500" style={{ width: `${weeklyPct}%` }} />
              </div>
              <span className="font-bold text-sm">{weeklyPct}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Fee Status */}
      {myFeeStructure && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Fee Status</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-3 text-center"><p className="">Total</p><p className="font-bold">{formatCurrency(totalDue)}</p></div>
            <div className="bg-green-50 rounded-lg p-3 text-center"><p className="text-green-600">Paid</p><p className="font-bold text-green-700">{formatCurrency(myCashPaid)}</p></div>
            <div className={(feePending > 0 ? 'bg-red-50' : 'bg-green-50') + ' rounded-lg p-3 text-center'}><p className={'' + (feePending > 0 ? 'text-red-600' : 'text-green-600')}>Pending</p><p className={'font-bold ' + (feePending > 0 ? 'text-red-700' : 'text-green-700')}>{feePending > 0 ? formatCurrency(feePending) : 'Cleared'}</p></div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Upcoming Exams</h3>
          {upcomingExams.length > 0 ? (
            <div className="space-y-2">
              {upcomingExams.map(exam => (
                <div key={exam.id} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                  <div><p className="text-sm font-medium">{exam.name}</p><p className="">{formatDate(exam.startDate)}</p></div>
                  <span className={'px-2 py-0.5 rounded-full font-medium capitalize ' + (exam.status === 'ongoing' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100')}>{exam.status}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-400 text-sm">No upcoming exams</p>}
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Announcements</h3>
          <div className="space-y-2">
            {[...myAnnouncements].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 4).map(ann => (
              <div key={ann.id} className="py-2 border-b border-gray-50 last:border-0">
                <p className="font-medium text-sm">{ann.title}</p>
                <p className="mt-0.5">{ann.content.slice(0, 80)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
export default ParentDashboard;
